/**
 * ContactWidget - Pannello laterale per creazione/modifica contatto
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 * Utilizza i componenti form unificati per garantire consistenza.
 */

import { useState } from 'react';
import { User, MessageCircle, Send } from 'lucide-react';
import { useHubStore } from '@business-assistant/shared';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import {
  TextField,
  EmailField,
  PhoneField,
  TextAreaField,
  TagsField,
  Button,
  formStyles,
} from '../common/forms';

interface ContactFormProps {
  isOpen: boolean;
  contactId?: string; // If provided, edit mode
  onClose: () => void;
  onSave: (data: ContactFormData) => void;
}

export interface ContactFormData {
  displayName: string;
  handles: {
    email?: string;
    phone?: string;
    whatsapp?: string;
    telegram?: string;
  };
  company?: string;
  role?: string;
  notes?: string;
  tags: string[];
}

export function ContactWidget({ isOpen, contactId, onClose, onSave }: ContactFormProps) {
  const { t } = useTranslation();
  const { contacts } = useHubStore();
  const existingContact = contactId ? contacts.find(c => c.id === contactId) : null;

  const [formData, setFormData] = useState<ContactFormData>({
    displayName: existingContact?.displayName || '',
    handles: {
      email: existingContact?.handles.email,
      phone: existingContact?.handles.phone,
      whatsapp: existingContact?.handles.whatsapp,
      telegram: existingContact?.handles.telegram,
    },
    company: existingContact?.company,
    role: existingContact?.role,
    notes: existingContact?.notes,
    tags: existingContact?.tags || [],
  });

  const handleSubmit = () => {
    if (!formData.displayName.trim()) return;
    onSave(formData);
  };

  const handleAddTag = (tag: string) => {
    setFormData({ ...formData, tags: [...formData.tags, tag] });
  };

  const handleRemoveTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const isEditMode = !!contactId;

  return (
    <DetailPanel
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? t('contacts.editContact') : t('contacts.newContact')}
      subtitle={isEditMode ? formData.displayName : t('contacts.addNewContact')}
      icon={<User size={18} />}
      headerColor="#3b82f6"
      width="50%"
      footer={
        <DetailActions>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isEditMode ? t('common.save') : t('contacts.createContact')}
          </Button>
        </DetailActions>
      }
    >
      {/* Basic Info */}
      <DetailSection title={t('contacts.information')}>
        <TextField
          name="displayName"
          label={t('contacts.displayName')}
          value={formData.displayName}
          onChange={(v) => setFormData({ ...formData, displayName: v })}
          placeholder="Es. Mario Rossi"
          required
          autoFocus
        />

        <div style={formStyles.row}>
          <div style={formStyles.column}>
            <TextField
              name="company"
              label={t('contacts.company')}
              value={formData.company || ''}
              onChange={(v) => setFormData({ ...formData, company: v })}
            />
          </div>
          <div style={formStyles.column}>
            <TextField
              name="role"
              label={t('contacts.role')}
              value={formData.role || ''}
              onChange={(v) => setFormData({ ...formData, role: v })}
            />
          </div>
        </div>
      </DetailSection>

      {/* Contact Handles */}
      <DetailSection title={t('contacts.contacts')}>
        <EmailField
          name="email"
          label={t('contacts.email')}
          value={formData.handles.email || ''}
          onChange={(v) => setFormData({
            ...formData,
            handles: { ...formData.handles, email: v }
          })}
        />

        <PhoneField
          name="phone"
          label={t('contacts.phone')}
          value={formData.handles.phone || ''}
          onChange={(v) => setFormData({
            ...formData,
            handles: { ...formData.handles, phone: v }
          })}
        />

        <PhoneField
          name="whatsapp"
          label="WhatsApp"
          value={formData.handles.whatsapp || ''}
          onChange={(v) => setFormData({
            ...formData,
            handles: { ...formData.handles, whatsapp: v }
          })}
          icon={MessageCircle}
        />

        <TextField
          name="telegram"
          label="Telegram"
          value={formData.handles.telegram || ''}
          onChange={(v) => setFormData({
            ...formData,
            handles: { ...formData.handles, telegram: v }
          })}
          placeholder="@username"
          icon={Send}
        />
      </DetailSection>

      {/* Notes */}
      <DetailSection title={t('contacts.notes')}>
        <TextAreaField
          name="notes"
          label=""
          value={formData.notes || ''}
          onChange={(v) => setFormData({ ...formData, notes: v })}
          rows={3}
        />
      </DetailSection>

      {/* Tags */}
      <DetailSection title={t('contacts.tags')}>
        <TagsField
          label=""
          tags={formData.tags}
          onAdd={handleAddTag}
          onRemove={handleRemoveTag}
          placeholder={t('contacts.addTag')}
        />
      </DetailSection>
    </DetailPanel>
  );
}
